import { type User as Auth0User } from '@auth0/auth0-spa-js';
import { IntlShape } from 'react-intl';

import { UserRolesIdsEnum, VerantosOrgEnum } from '@verantos-dev/core-enums';
import {
  type IVerantosUserRole,
  type IVerantosUser,
  UserInfoRequest,
} from '@verantos-dev/types-common';

import { AUTH0_AUDIENCE_URL } from './env';

const CODE_RE = /[?&]code=[^&]+/;
const STATE_RE = /[?&]state=[^&]+/;
const ERROR_RE = /[?&]error=[^&]+/;

export const hasAuthParams = (searchParams = window.location.search): boolean =>
  (CODE_RE.test(searchParams) || ERROR_RE.test(searchParams)) && STATE_RE.test(searchParams);

export const transformUser = (user: IVerantosUser): UserInfoRequest => {
  return {
    user_id: user.userId,
    alt_id: user.auth0Id,
    email: user.email,
    given_name: user.firstName || '',
    family_name: user.lastName || '',
    name: `${user.firstName} ${user.lastName}`,
    roles: user.roles.map((role) => role.roleId),
    email_verified: true,
    organization_id: user.organizationId,
    organization_name: user.organizationName,
  };
};

export const getUser = (user: Auth0User, intl?: IntlShape): IVerantosUser => {
  // Extract and transform roles
  let roles: IVerantosUserRole[] = [];
  if (intl) {
    roles =
      user[`${AUTH0_AUDIENCE_URL}/roles`]?.map((role: UserRolesIdsEnum) => ({
        roleId: role,
        displayName: getDisplayNameForRole(role, intl),
      })) || [];
  }

  // Extract other properties directly from the user object
  const organizationId = user[`${AUTH0_AUDIENCE_URL}/organizationId`];
  const organizationName = user[`${AUTH0_AUDIENCE_URL}/organizationName`];
  const firstName = user['given_name'];
  const lastName = user['family_name'];
  const userId = user[`${AUTH0_AUDIENCE_URL}/userId`];
  const auth0Id = user['sub'];
  const userInitials = getInitials(firstName || '', lastName || '');
  const email = user['email'];
  const phoneNumber = user['phone'];
  const isInternalUser = Boolean(
    organizationName && organizationName.toLowerCase() === VerantosOrgEnum.VERANTOS.toLowerCase()
  );

  // Return the processed user object
  return {
    roles,
    organizationId,
    organizationName,
    firstName,
    lastName,
    userId,
    auth0Id: auth0Id || '',
    userInitials,
    email: email || '',
    phoneNumber,
    isInternalUser,
    isAdmin: roles.some((role) => role.roleId === UserRolesIdsEnum.SA_ADMIN),
  };
};

const getInitials = (firstName?: string, lastName?: string) => {
  const firstInitial = firstName?.charAt(0)?.toUpperCase() ?? '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() ?? '';
  return firstInitial + lastInitial;
};

export const getDisplayNameForRole = (
  roleId: UserRolesIdsEnum,
  intl: IntlShape
): string | undefined => {
  switch (roleId) {
    case UserRolesIdsEnum.SA_ADMIN:
      return intl.formatMessage({ id: 'users.tableRole.sysAdmin' });
    case UserRolesIdsEnum.SA_ROOT:
      return intl.formatMessage({ id: 'users.tableRole.rootUser' });
    case UserRolesIdsEnum.OR_RESEARCHER:
      return intl.formatMessage({ id: 'users.tableRole.orResearcher' });
    case UserRolesIdsEnum.OR_STUDY_ADMIN:
      return intl.formatMessage({ id: 'users.tableRole.orStudyAdmin' });
    case UserRolesIdsEnum.VRN_DATA_ADMIN:
      return intl.formatMessage({ id: 'users.tableRole.vrnDataAdmin' });
    case UserRolesIdsEnum.VRN_RESEARCH_PARTNER:
      return intl.formatMessage({ id: 'users.tableRole.vrnResearchPartner' });
    case UserRolesIdsEnum.RDM_ANNOTATOR:
      return intl.formatMessage({ id: 'users.tableRole.rdmAnnotator' });
    case UserRolesIdsEnum.RDM_ANNOTATION_SUPERVISOR:
      return intl.formatMessage({ id: 'users.tableRole.rdmAnnotationSupervisor' });
    case UserRolesIdsEnum.ANNOTATION_MANAGER:
      return intl.formatMessage({ id: 'users.tableRole.annotationManager' });
    case UserRolesIdsEnum.ANNOTATION_USER:
      return intl.formatMessage({ id: 'users.tableRole.annotationUser' });
    case UserRolesIdsEnum.ACCURACY_VIEWER:
      return intl.formatMessage({ id: 'users.tableRole.accuracyViewer' });
    default:
      return roleId;
  }
};
